import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ZoomInOutlinedIcon from "@mui/icons-material/ZoomInOutlined";
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardMedia,
  Chip,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { memo, useMemo, useState } from "react";
import PropTypes from "prop-types";

import { resolveUploadUrl } from "../../../../../../infrastructure/utils/resolveUploadUrl";
import EditableImageField from "../../../../../components/shared/files/EditableImageField";
import FilePreviewLightbox from "../../../../../components/shared/files/FilePreviewLightbox";
import ImageDownloadButton from "../../../../../components/shared/files/ImageDownloadButton";
import { CRM_CARD_BODY_SX, CRM_CARD_HEADER_SX, CRM_CARD_SX, CRM_SECTION_TITLE_SX } from "./initialReviewStyles";
import { OPERATOR_CARD_SX, OPERATOR_GALLERY_GRID_SX } from "./operatorReviewStyles";

const ELECTRIC_BILL_FILE_ACCEPT = "image/*,application/pdf";

const getUploadAccept = (fieldKey) =>
  fieldKey === "electricBoillUrl" ? ELECTRIC_BILL_FILE_ACCEPT : "image/*";

const buildDownloadName = (filePrefix, label) =>
  filePrefix ? `${filePrefix}-${label}` : label;

const GalleryTile = memo(({ item, baseUrl, onZoom, large = false, showStatus = false, filePrefix = "" }) => {
  const src = item.url ? resolveUploadUrl(item.url, baseUrl) : "";
  const isPdf = item.kind === "pdf";
  const isMissing = !item.url;
  const previewHeight = large ? 220 : 140;

  return (
    <Card
      elevation={0}
      sx={{
        position: "relative",
        borderRadius: 2,
        border: "2px solid",
        borderColor: isMissing && item.required ? "error.light" : "divider",
        overflow: "hidden",
        height: "100%",
        bgcolor: isMissing ? "error.50" : "background.paper",
      }}
    >
      <CardActionArea
        onClick={() => {
          if (isPdf && src) {
            window.open(src, "_blank", "noopener,noreferrer");
            return;
          }
          if (src) onZoom({ src, title: item.label });
        }}
        sx={{ height: "100%" }}
        disabled={isMissing && !isPdf}
      >
        {showStatus ? (
          <Box sx={{ px: 1.25, pt: 1.25 }}>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              <Chip size="small" label={item.label} sx={{ fontWeight: 600 }} />
              {item.required ? (
                <Chip
                  size="small"
                  color={isMissing ? "error" : "success"}
                  variant={isMissing ? "filled" : "outlined"}
                  label={isMissing ? "الزامی — بارگذاری نشده" : "الزامی"}
                />
              ) : (
                <Chip
                  size="small"
                  variant="outlined"
                  color={isMissing ? "default" : "success"}
                  label={isMissing ? "بارگذاری نشده" : "موجود"}
                />
              )}
            </Stack>
          </Box>
        ) : null}

        {isPdf ? (
          <Box
            sx={{
              minHeight: large ? 160 : 120,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              p: 2,
              bgcolor: isMissing ? "transparent" : "grey.50",
            }}
          >
            <PictureAsPdfIcon color={isMissing ? "disabled" : "error"} />
            {!showStatus ? (
              <Typography variant="caption" fontWeight={600} textAlign="center">
                {item.label}
              </Typography>
            ) : null}
          </Box>
        ) : isMissing ? (
          <Box
            sx={{
              minHeight: previewHeight,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "grey.100",
              mx: 1.25,
              mb: 1.25,
              borderRadius: 1,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              فایل موجود نیست
            </Typography>
          </Box>
        ) : (
          <>
            <CardMedia
              component="img"
              height={previewHeight}
              image={src || undefined}
              alt={item.label}
              loading="lazy"
              sx={{ objectFit: "cover", bgcolor: "grey.100" }}
            />
            {!showStatus ? (
              <Box sx={{ p: 1.25 }}>
                <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.4 }}>
                  {item.label}
                </Typography>
              </Box>
            ) : null}
          </>
        )}
      </CardActionArea>
      {!isPdf && src ? (
        <ImageDownloadButton
          src={src}
          fileName={buildDownloadName(filePrefix, item.label)}
          sx={{ position: "absolute", top: 8, left: 8, zIndex: 1, boxShadow: 1 }}
        />
      ) : null}
    </Card>
  );
});

GalleryTile.displayName = "GalleryTile";

GalleryTile.propTypes = {
  item: PropTypes.object.isRequired,
  baseUrl: PropTypes.string,
  onZoom: PropTypes.func.isRequired,
  large: PropTypes.bool,
  showStatus: PropTypes.bool,
  filePrefix: PropTypes.string,
};

const MediaGalleryCard = memo(
  ({
    values,
    fileBaseUrl,
    fieldDisabled,
    handleFileChange,
    errors,
    touched,
    editable = true,
    layout = "grid",
  }) => {
    const [lightbox, setLightbox] = useState({ open: false, src: "", title: "" });
    const filePrefix = String(
      values.referenceID ?? values.referenceId ?? values.id ?? ""
    );

    const galleryItems = useMemo(() => {
      const items = [
        {
          key: "plantInstallImage1Url",
          label: "تصویر محل نصب ۱",
          url: values.plantInstallImage1Url,
          kind: "image",
          required: true,
        },
        {
          key: "plantInstallImage2Url",
          label: "تصویر محل نصب ۲",
          url: values.plantInstallImage2Url,
          kind: "image",
          required: true,
        },
        {
          key: "electricBoillUrl",
          label: "تصویر یا PDF قبض برق",
          url: values.electricBoillUrl,
          kind: /\.pdf(\?|$)/i.test(String(values.electricBoillUrl || ""))
            ? "pdf"
            : "image",
          required: true,
        },
        {
          key: "nationalCardUrl",
          label: "کارت ملی",
          url: values.nationalCardUrl,
          kind: "image",
          alwaysEditable: true,
        },
        {
          key: "birthCertificateUrl",
          label: "شناسنامه",
          url: values.birthCertificateUrl,
          kind: "image",
          alwaysEditable: true,
        },
        {
          key: "nationalCardBackUrl",
          label: "پشت کارت ملی",
          url: values.nationalCardBackUrl,
          kind: "image",
          alwaysEditable: true,
        },
        {
          key: "homeOwnerShipDocUrl",
          label: "سند مالکیت",
          url: values.homeOwnerShipDocUrl,
          kind: "image",
        },
        {
          key: "boardConsentDocumentUrl",
          label: "رضایت‌نامه هیئت‌مدیره",
          url: values.boardConsentDocumentUrl || values.BoardConsentDocumentUrl,
          kind: "pdf",
        },
        {
          key: "authorizedRepresentativeLetterUrl",
          label: "نامه معرفی نماینده",
          url:
            values.authorizedRepresentativeLetterUrl ||
            values.AuthorizedRepresentativeLetterUrl,
          kind: "pdf",
        },
        {
          key: "agreementDocumentUrl",
          label: "تفاهم‌نامه همکاری",
          url: values.agreementDocumentUrl || values.AgreementDocumentUrl,
          kind: "pdf",
        },
      ];
      return items.filter(
        (item) => item.url || item.required || item.alwaysEditable
      );
    }, [values]);

    const identityDocKeys = [
      "nationalCardUrl",
      "birthCertificateUrl",
      "nationalCardBackUrl",
    ];

    const primaryImages = galleryItems.filter((item) =>
      [
        "plantInstallImage1Url",
        "plantInstallImage2Url",
        "electricBoillUrl",
        ...identityDocKeys,
      ].includes(item.key)
    );
    const extraItems = galleryItems.filter(
      (item) =>
        ![
          "plantInstallImage1Url",
          "plantInstallImage2Url",
          "electricBoillUrl",
          ...identityDocKeys,
        ].includes(item.key) && (item.url || item.kind === "pdf")
    );

    const visibleItems = galleryItems.filter((i) => i.url);
    const isCarousel = layout === "carousel";
    const isReview = layout === "review";
    const primaryOrder = [
      "electricBoillUrl",
      "plantInstallImage1Url",
      "plantInstallImage2Url",
      ...identityDocKeys,
    ];
    const reviewPrimary = primaryOrder
      .map((key) => galleryItems.find((i) => i.key === key))
      .filter(Boolean);
    const reviewOthers = galleryItems.filter(
      (item) =>
        !primaryOrder.includes(item.key) &&
        (item.url || item.required || item.alwaysEditable)
    );

    return (
      <Paper elevation={0} sx={isReview ? OPERATOR_CARD_SX : CRM_CARD_SX}>
        <Box sx={CRM_CARD_HEADER_SX}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography sx={CRM_SECTION_TITLE_SX}>تصاویر و مدارک</Typography>
            <Chip size="small" label={`${visibleItems.length} فایل`} variant="outlined" />
          </Stack>
        </Box>
        <Box sx={CRM_CARD_BODY_SX}>
          {isCarousel && visibleItems.length > 0 ? (
            <Box
              sx={{
                display: "flex",
                gap: 2,
                overflowX: "auto",
                pb: 1,
                mb: editable ? 2 : 0,
                scrollSnapType: "x mandatory",
                "& > *": { scrollSnapAlign: "start", flex: "0 0 180px" },
              }}
            >
              {visibleItems.map((item) => (
                <GalleryTile
                  key={item.key}
                  item={item}
                  baseUrl={fileBaseUrl}
                  filePrefix={filePrefix}
                  onZoom={({ src, title }) => setLightbox({ open: true, src, title })}
                />
              ))}
            </Box>
          ) : null}

          {editable && isCarousel ? (
            <Grid container spacing={2}>
              {primaryImages.map((item) => (
                <Grid item xs={12} sm={4} key={item.key}>
                  <EditableImageField
                    label={item.label}
                    value={values[item.key]}
                    baseUrl={fileBaseUrl}
                    disabled={fieldDisabled}
                    downloadable
                    downloadFileName={buildDownloadName(filePrefix, item.label)}
                    accept={getUploadAccept(item.key)}
                    onChange={handleFileChange(item.key)}
                    error={errors[item.key] && touched[item.key]}
                    helperText={
                      errors[item.key] && touched[item.key] && errors[item.key]
                    }
                    required={item.required}
                  />
                </Grid>
              ))}
            </Grid>
          ) : null}

          {!isCarousel && !isReview && editable ? (
            <Grid container spacing={2} sx={{ mb: extraItems.length ? 2 : 0 }}>
              {primaryImages.map((item) => (
                <Grid item xs={12} sm={6} md={4} key={item.key}>
                  <EditableImageField
                    label={item.label}
                    value={values[item.key]}
                    baseUrl={fileBaseUrl}
                    disabled={fieldDisabled}
                    downloadable
                    downloadFileName={buildDownloadName(filePrefix, item.label)}
                    accept={getUploadAccept(item.key)}
                    onChange={handleFileChange(item.key)}
                    error={errors[item.key] && touched[item.key]}
                    helperText={
                      errors[item.key] && touched[item.key] && errors[item.key]
                    }
                    required={item.required}
                  />
                </Grid>
              ))}
            </Grid>
          ) : null}

          {isReview ? (
            <>
              <Box sx={{ ...OPERATOR_GALLERY_GRID_SX, mb: 2 }}>
                {reviewPrimary.map((item) => (
                  <GalleryTile
                    key={item.key}
                    item={item}
                    baseUrl={fileBaseUrl}
                    filePrefix={filePrefix}
                    large
                    showStatus
                    onZoom={({ src, title }) =>
                      setLightbox({ open: true, src, title })
                    }
                  />
                ))}
              </Box>
              {editable ? (
                <Box
                  sx={{
                    ...OPERATOR_GALLERY_GRID_SX,
                    mb: reviewOthers.length ? 2 : 0,
                  }}
                >
                  {reviewPrimary.map((item) => (
                    <EditableImageField
                      key={`edit-${item.key}`}
                      label={item.label}
                      value={values[item.key]}
                      baseUrl={fileBaseUrl}
                      disabled={fieldDisabled}
                      downloadable
                      downloadFileName={buildDownloadName(filePrefix, item.label)}
                      accept={getUploadAccept(item.key)}
                      onChange={handleFileChange(item.key)}
                      error={errors[item.key] && touched[item.key]}
                      helperText={
                        errors[item.key] && touched[item.key] && errors[item.key]
                      }
                      required={item.required}
                    />
                  ))}
                </Box>
              ) : null}
              {reviewOthers.length ? (
                <Box sx={OPERATOR_GALLERY_GRID_SX}>
                  {reviewOthers.map((item) => (
                    <GalleryTile
                      key={item.key}
                      item={item}
                      baseUrl={fileBaseUrl}
                      filePrefix={filePrefix}
                      large
                      showStatus
                      onZoom={({ src, title }) =>
                        setLightbox({ open: true, src, title })
                      }
                    />
                  ))}
                </Box>
              ) : null}
            </>
          ) : null}

          {!isCarousel && !isReview ? (
          <Grid container spacing={2}>
            {(editable ? extraItems : galleryItems.filter((i) => i.url)).map((item) => (
              <Grid item xs={6} sm={4} md={3} key={item.key}>
                <GalleryTile
                  item={item}
                  baseUrl={fileBaseUrl}
                  filePrefix={filePrefix}
                  onZoom={({ src, title }) =>
                    setLightbox({ open: true, src, title })
                  }
                />
                {editable && item.kind === "pdf" && item.url ? (
                  <Button
                    size="small"
                    fullWidth
                    sx={{ mt: 0.5 }}
                    startIcon={<VisibilityIcon fontSize="small" />}
                    href={resolveUploadUrl(item.url, fileBaseUrl)}
                    target="_blank"
                  >
                    مشاهده PDF
                  </Button>
                ) : null}
              </Grid>
            ))}
          </Grid>
          ) : null}

          {!galleryItems.some((i) => i.url) ? (
            <Typography variant="caption" color="text.secondary">
              فایلی آپلود نشده است.
            </Typography>
          ) : null}

          <Stack direction="row" spacing={0.5} sx={{ mt: 1.5 }} alignItems="center">
            <ZoomInOutlinedIcon sx={{ fontSize: 16, color: "text.secondary" }} />
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
              برای بزرگ‌نمایی روی تصویر کلیک کنید
            </Typography>
          </Stack>
        </Box>

        <FilePreviewLightbox
          open={lightbox.open}
          onClose={() => setLightbox({ open: false, src: "", title: "" })}
          url={lightbox.src}
          label={lightbox.title}
          fileBaseUrl={fileBaseUrl}
        />
      </Paper>
    );
  }
);

MediaGalleryCard.displayName = "MediaGalleryCard";

MediaGalleryCard.propTypes = {
  values: PropTypes.object.isRequired,
  fileBaseUrl: PropTypes.string,
  fieldDisabled: PropTypes.bool,
  handleFileChange: PropTypes.func.isRequired,
  errors: PropTypes.object,
  touched: PropTypes.object,
  editable: PropTypes.bool,
  layout: PropTypes.oneOf(["carousel", "grid", "review"]),
};

export default MediaGalleryCard;
